// src/components/Carriers.js
// Transporteurs — /carriers-instructions. The account's carriers (a carrier
// is its canonical name; its services live inside it, from the ticket
// stats), how the agent reaches each one (portal, form, API or e-mail), what
// blocks it (portal account missing or refused, login-code mailbox missing,
// no address) and the carrier messages (general instructions, sending
// mailbox). Per-carrier behaviour is configuration (carriersModel.js reads
// the descriptor registry), never a name test here.
// URL: ?q= search, ?flag= missing_credentials | missing_otp | missing_rules,
// ?page=, ?view=messages. Copilot anchors (data-tour) are listed in the
// backend copilot_catalog — keep them.
// API: GET /carriers, /carrier-descriptors, /carrier-credentials/{id}/all,
// /carriers/{id}/otp-mailbox, /carrier-mail-sender, /carrier-message-config,
// /carrier-requests, /api/statistics/carriers (30 days); PUT
// /carrier-message-config; POST /carriers, /carrier-credentials,
// /carrier-requests; GET /carrier-catalog when adding.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartColumn, ChevronRight, FileText, Globe, Inbox, KeyRound, Mail, Plus, RefreshCw, Search, Send, ShieldCheck,
  TriangleAlert, Truck, X,
} from 'lucide-react';
import getCarrierLogo from '../utils/carrierLogos';
import {
  Badge, Banner, Card, EmptyState, Field, IconChip, Kpi, Modal, PageHeader, Pagination, Segmented, Switch, Tabs, Toast,
} from '../design/ui';
import {
  FLAGS, UNCONFIGURED_MIN_TICKETS, carrierFacts, carriersAttention, formatCount, formatShare, matchesFlag, unconfiguredCarriers, windowStart,
} from './carriersModel';
import './CarriersInstructions.css';

const PAGE_SIZE = 25;
const ATTENTION_PREVIEW = 5;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const emptyNewCarrier = {
  name: '',
  emails: [],
  contact_rules: '',
  message_instructions: '',
  automation: false,
  is_mail: false,
  cc_emails: [],
  credentials: { username: '', password: '' },
  isAutomated: false,
  fromCatalog: false,
  requiresCredentials: false,
};

export const CarrierLogo = ({ name, size }) => {
  const logo = getCarrierLogo(name);
  return (
    <span className={`u-logo carriers-v2__logo${size === 'sm' ? ' carriers-v2__logo--sm' : ''}`} aria-hidden="true">
      {logo ? <img src={logo} alt="" loading="lazy" /> : <Truck strokeWidth={1.8} />}
    </span>
  );
};

const MODE_ICONS = { portal: Globe, form: FileText, api: Send, email: Mail, email_portal: Mail };

export const ModeBadge = ({ mode, t }) => {
  const Icon = MODE_ICONS[mode] || Globe;
  const labels = {
    portal: t('carriersUi.mode.portal', 'Portail'),
    form: t('carriersUi.mode.form', 'Formulaire'),
    api: t('carriersUi.mode.api', 'API'),
    email: t('carriersUi.mode.email', 'E-mail'),
    email_portal: t('carriersUi.mode.emailPortal', 'E-mail puis portail'),
  };
  return <Badge tone={mode === 'email' || mode === 'email_portal' ? 'info' : 'accent'} icon={Icon}>{labels[mode] || mode}</Badge>;
};

const Carriers = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  // Plural keys (_one/_other in the locale files); the French default follows the count.
  const tp = (key, one, other, options) => t(key, options.count === 1 ? one : other, options);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') || '';
  const flag = FLAGS.includes(searchParams.get('flag')) ? searchParams.get('flag') : '';
  const view = searchParams.get('view') === 'messages' ? 'messages' : 'list';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateParams = useCallback((updates, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  }, [setSearchParams]);

  // ------------------------------------------------------------ data
  const [carriers, setCarriers] = useState(null); // null = loading
  const [loadError, setLoadError] = useState('');
  const [descriptors, setDescriptors] = useState(null); // null = loading, [] = unavailable
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [credentials, setCredentials] = useState({});
  const [otp, setOtp] = useState({});
  const [mailSender, setMailSender] = useState(undefined);
  const [messageConfig, setMessageConfig] = useState(null);
  const [generalInstructions, setGeneralInstructions] = useState('');
  const [messageConfigSaving, setMessageConfigSaving] = useState(false);
  const [messageConfigError, setMessageConfigError] = useState(null);
  const [carrierRequests, setCarrierRequests] = useState([]);
  const [toast, setToast] = useState(null);
  const [showAllAttention, setShowAllAttention] = useState(false);
  const fetched = useRef({ credentials: new Set(), otp: new Set() });

  const fetchCarriers = useCallback(async () => {
    setLoadError('');
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders() });
      setCarriers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carriers:', error?.response?.status || error?.message);
      setLoadError(t('carriersUi.list.loadError', 'Impossible de charger les transporteurs.'));
      setCarriers([]);
    }
  }, [t]);

  const fetchDescriptors = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/carrier-descriptors', { headers: authHeaders() });
      setDescriptors(Array.isArray(response.data?.descriptors) ? response.data.descriptors : []);
    } catch (error) {
      console.error('Failed to fetch carrier descriptors:', error?.response?.status || error?.message);
      setDescriptors([]);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`/api/statistics/carriers?start_date=${windowStart(30)}&limit=100`, { headers: authHeaders() });
      setStats(response.data && Array.isArray(response.data.carriers) ? response.data : null);
    } catch (error) {
      console.error('Failed to fetch carrier stats:', error?.response?.status || error?.message);
      setStats(null);
    }
  }, []);

  const fetchMessageConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/carrier-message-config', { headers: authHeaders() });
      setMessageConfig(response.data);
      setGeneralInstructions(response.data?.general_instructions || '');
    } catch (error) {
      console.error('Failed to fetch carrier message config:', error?.response?.status || error?.message);
    }
  }, []);

  const fetchMailSender = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/carrier-mail-sender', { headers: authHeaders() });
      setMailSender(response.data || null);
    } catch (error) {
      console.error('Failed to fetch the carrier mail sender:', error?.response?.status || error?.message);
      setMailSender(null);
    }
  }, []);

  const fetchCarrierRequests = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/carrier-requests', { headers: authHeaders() });
      setCarrierRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carrier requests:', error?.response?.status || error?.message);
    }
  }, []);

  useEffect(() => {
    fetchCarriers();
    fetchDescriptors();
    fetchStats();
    fetchMessageConfig();
    fetchMailSender();
    fetchCarrierRequests();
  }, [fetchCarriers, fetchDescriptors, fetchStats, fetchMessageConfig, fetchMailSender, fetchCarrierRequests]);

  // Portal accounts and login-code mailboxes, only for the carriers whose
  // configuration needs them (one bounded GET each, once per carrier).
  useEffect(() => {
    if (!carriers || descriptors === null) return;
    carriers.forEach((carrier) => {
      const facts = carrierFacts(carrier, { descriptors });
      if (facts.needsAccount !== false && !fetched.current.credentials.has(carrier.id)) {
        fetched.current.credentials.add(carrier.id);
        axios.get(`/api/logistics/carrier-credentials/${carrier.id}/all`, { headers: authHeaders() })
          .then((response) => setCredentials((prev) => ({ ...prev, [carrier.id]: Array.isArray(response.data) ? response.data : [] })))
          .catch((error) => console.error('Failed to fetch carrier credentials:', error?.response?.status || error?.message));
      }
      if (facts.needsOtp && !fetched.current.otp.has(carrier.id)) {
        fetched.current.otp.add(carrier.id);
        axios.get(`/api/logistics/carriers/${carrier.id}/otp-mailbox`, { headers: authHeaders() })
          .then((response) => setOtp((prev) => ({ ...prev, [carrier.id]: response.data || { configured: false } })))
          .catch((error) => console.error('Failed to fetch the OTP mailbox:', error?.response?.status || error?.message));
      }
    });
  }, [carriers, descriptors]);

  const refresh = () => {
    fetched.current = { credentials: new Set(), otp: new Set() };
    setCredentials({});
    setOtp({});
    setCarriers(null);
    setStats(undefined);
    fetchCarriers();
    fetchStats();
    fetchMailSender();
    fetchCarrierRequests();
  };

  // ------------------------------------------------------------ derived
  const statsRows = stats ? stats.carriers : undefined;
  const factsById = useMemo(() => {
    const map = {};
    (carriers || []).forEach((carrier) => {
      map[carrier.id] = carrierFacts(carrier, { descriptors: descriptors || [], credentials, otp, statsRows });
    });
    return map;
  }, [carriers, descriptors, credentials, otp, statsRows]);

  const all = useMemo(() => carriers || [], [carriers]);
  const pendingRequests = carrierRequests.filter((request) => request.status === 'pending');
  const unconfigured = useMemo(
    () => (carriers ? unconfiguredCarriers(statsRows, carriers, descriptors || [], pendingRequests.map((r) => r.carrier_name)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statsRows, carriers, descriptors, carrierRequests],
  );
  const attention = useMemo(
    () => (carriers ? carriersAttention({ carriers, factsById, mailSender, unconfigured }) : []),
    [carriers, factsById, mailSender, unconfigured],
  );

  const counts = useMemo(() => {
    const facts = all.map((carrier) => factsById[carrier.id]).filter(Boolean);
    const needAccount = facts.filter((f) => f.needsAccount === true);
    const needOtp = facts.filter((f) => f.needsOtp);
    return {
      total: all.length,
      byMode: facts.reduce((acc, f) => {
        const key = f.mode === 'email_portal' ? 'email' : f.mode;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
      needAccount: needAccount.length,
      accountsLoaded: needAccount.every((f) => f.accountsLoaded),
      accountsOk: needAccount.filter((f) => f.accountsLoaded && f.accounts.length > 0 && f.invalidAccounts.length === 0).length,
      missingCredentials: facts.filter((f) => matchesFlag(f, 'missing_credentials')).length,
      needOtp: needOtp.length,
      otpLoaded: needOtp.every((f) => f.otp !== undefined),
      otpOk: needOtp.filter((f) => f.otp?.configured).length,
      missingOtp: facts.filter((f) => f.missingOtp).length,
      missingRules: facts.filter((f) => f.missingRules).length,
    };
  }, [all, factsById]);

  const filtered = all
    .filter((carrier) => (carrier.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((carrier) => !flag || (factsById[carrier.id] && matchesFlag(factsById[carrier.id], flag)))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const maxTickets = Math.max(1, ...all.map((carrier) => factsById[carrier.id]?.activity?.count || 0));

  const flagLabel = (value) => ({
    missing_credentials: t('carriersUi.list.flagAccount', 'Compte à régler'),
    missing_otp: t('carriersUi.list.flagOtp', 'Codes à connecter'),
    missing_rules: t('carriersUi.list.flagRules', 'Sans consignes'),
  }[value] || value);

  const toggleFlag = (value) => updateParams({ flag: flag === value ? null : value, page: null, view: null });

  // ------------------------------------------------------------ messages tab
  const handleSaveMessageConfig = async () => {
    setMessageConfigSaving(true);
    setMessageConfigError(null);
    try {
      const response = await axios.put(
        '/api/logistics/carrier-message-config',
        { general_instructions: generalInstructions },
        { headers: authHeaders() },
      );
      setMessageConfig((prev) => ({ ...prev, ...response.data }));
      setGeneralInstructions(response.data?.general_instructions || '');
      setToast({ tone: 'success', text: t('carriersUi.messages.saved', 'Consignes des messages enregistrées.') });
    } catch (error) {
      setMessageConfigError(error.response?.data?.detail || error.message || t('carriersUi.saveError', 'L’enregistrement a échoué.'));
    } finally {
      setMessageConfigSaving(false);
    }
  };

  // ------------------------------------------------------------ add a carrier
  const [showSelectionOverlay, setShowSelectionOverlay] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [carrierCatalog, setCarrierCatalog] = useState(null);
  const [newCarrier, setNewCarrier] = useState(emptyNewCarrier);
  const [createError, setCreateError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [requestModal, setRequestModal] = useState(null); // { type, nameLocked, carrierId }
  const [requestDetails, setRequestDetails] = useState('');
  const [requestCarrierName, setRequestCarrierName] = useState('');
  const [requestStatus, setRequestStatus] = useState(null); // null | sending | sent | duplicate | error

  const fetchCarrierCatalog = async () => {
    try {
      const response = await axios.get('/api/logistics/carrier-catalog', { headers: authHeaders() });
      setCarrierCatalog(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carrier catalog:', error?.response?.status || error?.message);
      setCarrierCatalog([]);
    }
  };

  const handleCreateButtonClick = async () => {
    setShowSelectionOverlay(true);
    await Promise.all([fetchCarrierCatalog(), fetchCarrierRequests()]);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowSelectionOverlay(false);
    setCreateError(null);
  };

  const tenantCarrierNames = new Set(all.map((c) => (c.name || '').trim().toLowerCase()));
  const isCatalogCarrierAdded = (entry) => tenantCarrierNames.has(entry.name.trim().toLowerCase());

  const handleSelectCatalogCarrier = (entry) => {
    if (isCatalogCarrierAdded(entry)) return;
    const isWeb = entry.integration_mode === 'web_automation';
    setNewCarrier({
      ...emptyNewCarrier,
      name: entry.name,
      // E-mail carriers start with one empty address field ready to fill.
      emails: isWeb ? [] : [''],
      is_mail: !isWeb,
      isAutomated: isWeb,
      fromCatalog: true,
      requiresCredentials: Boolean(entry.requires_credentials),
    });
    setCreateError(null);
    setShowSelectionOverlay(false);
    setShowCreateModal(true);
  };

  const handleSelectManualCreation = (name = '') => {
    setNewCarrier({ ...emptyNewCarrier, name, is_mail: true, emails: [''] });
    setCreateError(null);
    setShowSelectionOverlay(false);
    setShowCreateModal(true);
  };

  const handleCreateCarrier = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const headers = authHeaders();
      const payload = {
        name: newCarrier.name,
        emails: newCarrier.emails.filter((email) => email && email.trim()),
        contact_rules: newCarrier.contact_rules,
        message_instructions: newCarrier.message_instructions || null,
        automation: newCarrier.automation,
        is_mail: newCarrier.is_mail,
        cc_emails: newCarrier.cc_emails || [],
      };
      const response = await axios.post('/api/logistics/carriers', payload, { headers });

      // Portal credentials go through the dedicated credentials API — the
      // create endpoint ignores a `credentials` field silently.
      const { username, password } = newCarrier.credentials || {};
      let credentialsFailed = false;
      if (newCarrier.requiresCredentials && username && password && response.data?.id) {
        try {
          await axios.post('/api/logistics/carrier-credentials', {
            carrier_id: response.data.id,
            username,
            password,
            label: null,
            client_ids: [],
            additional_fields: {},
          }, { headers });
        } catch (credError) {
          console.error('Carrier created but saving credentials failed:', credError?.response?.status || credError?.message);
          credentialsFailed = true;
        }
      }

      setShowCreateModal(false);
      setNewCarrier(emptyNewCarrier);
      setToast(credentialsFailed
        ? { tone: 'danger', text: t('carriersUi.add.credentialsFailed', '{{name}} est ajouté, mais son compte portail n’a pas été enregistré : ajoutez-le sur sa page.', { name: payload.name }) }
        : { tone: 'success', text: t('carriersUi.add.created', '{{name}} est ajouté.', { name: payload.name }) });
      fetched.current.credentials.delete(response.data?.id);
      fetchCarriers();
    } catch (error) {
      setCreateError(error.response?.data?.detail || error.message || t('carriersUi.add.createError', 'Le transporteur n’a pas pu être ajouté.'));
    } finally {
      setIsCreating(false);
    }
  };

  const openRequestModal = ({ type, carrierName = '', nameLocked = false, carrierId = null }) => {
    setRequestModal({ type, nameLocked, carrierId });
    setRequestCarrierName(carrierName);
    setRequestDetails('');
    setRequestStatus(null);
  };

  const handleSubmitCarrierRequest = async () => {
    if (!requestCarrierName.trim()) return;
    setRequestStatus('sending');
    try {
      await axios.post('/api/logistics/carrier-requests', {
        carrier_name: requestCarrierName.trim(),
        request_type: requestModal.type,
        details: requestDetails.trim() || null,
        carrier_id: requestModal.carrierId,
      }, { headers: authHeaders() });
      setRequestStatus('sent');
      fetchCarrierRequests();
    } catch (error) {
      setRequestStatus(error.response?.status === 409 ? 'duplicate' : 'error');
    }
  };

  const setNewEmails = (emails) => setNewCarrier((prev) => ({ ...prev, emails }));

  // ------------------------------------------------------------ zones
  const loading = carriers === null;
  const portalCount = (counts.byMode.portal || 0) + (counts.byMode.form || 0) + (counts.byMode.api || 0);
  const carrierIssues = attention.filter((item) => item.carrier).map((item) => item.carrier.id);
  const blockedCarriers = new Set(carrierIssues).size;

  const subtitle = loading || counts.total === 0
    ? t('carriersUi.list.subtitle', 'Les transporteurs de vos colis : comment l’agent les contacte, leurs comptes portail et leurs consignes.')
    : [
      tp('carriersUi.list.subtitleCount', '{{count}} transporteur', '{{count}} transporteurs', { count: counts.total }),
      t('carriersUi.list.subtitleModes', '{{portal}} par portail, formulaire ou API · {{email}} par e-mail', { portal: portalCount, email: counts.byMode.email || 0 }),
      blockedCarriers > 0 && tp('carriersUi.list.subtitleBlocked', '{{count}} demande une action', '{{count}} demandent une action', { count: blockedCarriers }),
    ].filter(Boolean).join(' · ');

  const attentionTitle = (item) => {
    switch (item.kind) {
      case 'mailSender': return t('carriersUi.attention.mailSender', 'La boîte d’envoi des e-mails transporteur ne fonctionne plus');
      case 'invalidAccount': return t('carriersUi.attention.invalidAccount', 'Le portail {{carrier}} refuse le compte « {{label}} »', { carrier: item.carrier.name, label: item.account.label || item.account.username });
      case 'missingAccount': return t('carriersUi.attention.missingAccount', '{{carrier}} n’a pas de compte portail', { carrier: item.carrier.name });
      case 'missingOtp': return t('carriersUi.attention.missingOtp', '{{carrier}} demande un code de connexion par e-mail', { carrier: item.carrier.name });
      case 'missingAddress': return t('carriersUi.attention.missingAddress', '{{carrier}} n’a aucune adresse e-mail', { carrier: item.carrier.name });
      case 'unconfigured': return tp('carriersUi.attention.unconfigured', '{{count}} transporteur de vos tickets n’est pas configuré', '{{count}} transporteurs de vos tickets ne sont pas configurés', { count: item.carriers.length });
      default: return '';
    }
  };

  const attentionDetail = (item) => {
    switch (item.kind) {
      case 'mailSender': return item.detail;
      case 'invalidAccount': return t('carriersUi.attention.invalidAccountHint', 'L’agent ne peut plus se connecter à son portail avec ces identifiants : mettez à jour le mot de passe.');
      case 'missingAccount': return t('carriersUi.attention.missingAccountHint', 'L’agent ne peut pas déposer de demande sur son portail sans identifiants.');
      case 'missingOtp': return t('carriersUi.attention.missingOtpHint', 'Connectez la boîte qui reçoit ces codes, sinon la connexion au portail échoue.');
      case 'missingAddress': return t('carriersUi.attention.missingAddressHint', 'L’agent le contacte par e-mail mais n’a aucune adresse où écrire.');
      case 'unconfigured': return t('settingsPolishUi.carriers.unconfiguredHint', 'Au moins {{min}} tickets sur 30 jours : {{list}}. L’agent ne peut pas les contacter tant qu’ils ne sont pas ajoutés.', {
        min: UNCONFIGURED_MIN_TICKETS,
        list: item.carriers.slice(0, 6).map((c) => `${c.name} (${formatCount(c.count, lang)})`).join(', '),
      });
      default: return '';
    }
  };

  const attentionAction = (item) => {
    if (item.kind === 'mailSender') {
      return { label: t('carriersUi.attention.openParameters', 'Ouvrir Paramètres'), onClick: () => navigate('/parameters?tab=integrations') };
    }
    if (item.kind === 'unconfigured') {
      return { label: t('carriersInstructions.addCarrier', 'Ajouter un transporteur'), onClick: handleCreateButtonClick };
    }
    return { label: t('carriersUi.see', 'Voir'), onClick: () => navigate(`/carriers-instructions/${item.carrier.id}`) };
  };

  const renderAttention = () => {
    if (loading || attention.length === 0) return null;
    const visible = showAllAttention ? attention : attention.slice(0, ATTENTION_PREVIEW);
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('carriersUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="carriers-v2__attention"
      >
        <ul className="u-list">
          {visible.map((item) => {
            const action = attentionAction(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'danger' ? t('carriersUi.attention.blocking', 'Bloquant') : t('carriersUi.attention.watch', 'À surveiller')}
                </Badge>
                {item.carrier
                  ? <CarrierLogo name={item.carrier.name} size="sm" />
                  : <IconChip icon={item.kind === 'mailSender' ? Inbox : Truck} tone="neutral" size="sm" />}
                <div className="u-row__main">
                  <div className="u-row__title">{attentionTitle(item)}</div>
                  <div className="u-row__meta u-row__meta--wrap">{attentionDetail(item)}</div>
                </div>
                <div className="u-row__side">
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={action.onClick}>
                    {action.label}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {attention.length > ATTENTION_PREVIEW && (
          <div className="carriers-v2__attention-more">
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setShowAllAttention((v) => !v)}>
              {showAllAttention
                ? t('carriersUi.attention.showLess', 'Afficher moins')
                : tp('carriersUi.attention.showMore', 'Afficher l’autre point', 'Afficher les {{count}} autres points', { count: attention.length - ATTENTION_PREVIEW })}
            </button>
          </div>
        )}
      </Card>
    );
  };

  const renderKpis = () => {
    if (loading) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton carriers-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (counts.total === 0) return null;
    const top = stats?.carriers?.[0];
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={Truck}
          label={t('carriersUi.kpi.carriers', 'Transporteurs')}
          value={formatCount(counts.total, lang)}
          context={t('carriersUi.kpi.carriersContext', '{{portal}} par portail · {{email}} par e-mail', { portal: portalCount, email: counts.byMode.email || 0 })}
          active={!flag && view === 'list'}
          onClick={() => updateParams({ flag: null, page: null, view: null })}
        />
        {stats === undefined && <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />}
        {stats && (
          <Kpi
            icon={ChartColumn}
            tone="info"
            label={t('carriersUi.kpi.tickets', 'Tickets 30 jours')}
            value={formatCount(stats.total_with_carrier, lang)}
            context={top
              ? t('carriersUi.kpi.ticketsContext', '{{name}} en tête · {{share}}', { name: top.carrier, share: formatShare(top.percentage, lang) })
              : t('carriersUi.kpi.ticketsNone', 'aucun sur 30 jours')}
            onClick={() => navigate('/statistics?days=30')}
            title={t('carriersUi.kpi.ticketsTitle', 'Ouvrir les statistiques')}
          />
        )}
        {counts.needAccount > 0 && (counts.accountsLoaded ? (
          <Kpi
            icon={KeyRound}
            tone={counts.missingCredentials > 0 ? 'warning' : 'success'}
            label={t('carriersUi.kpi.accounts', 'Comptes portail')}
            value={`${counts.accountsOk} / ${counts.needAccount}`}
            context={counts.missingCredentials > 0
              ? tp('carriersUi.kpi.accountsMissing', '{{count}} sans compte valide', '{{count}} sans compte valide', { count: counts.missingCredentials })
              : t('carriersUi.kpi.accountsOk', 'chaque portail a son compte')}
            contextTone={counts.missingCredentials > 0 ? 'warning' : undefined}
            meter={{ value: counts.accountsOk, max: counts.needAccount, tone: counts.missingCredentials > 0 ? 'warning' : 'success', label: t('carriersUi.kpi.accounts', 'Comptes portail') }}
            active={flag === 'missing_credentials'}
            onClick={() => toggleFlag('missing_credentials')}
          />
        ) : <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />)}
        {counts.needOtp > 0 && (counts.otpLoaded ? (
          <Kpi
            icon={ShieldCheck}
            tone={counts.missingOtp > 0 ? 'warning' : 'success'}
            label={t('carriersUi.kpi.otp', 'Boîtes de codes')}
            value={`${counts.otpOk} / ${counts.needOtp}`}
            context={counts.missingOtp > 0
              ? tp('carriersUi.kpi.otpMissing', '{{count}} boîte à connecter', '{{count}} boîtes à connecter', { count: counts.missingOtp })
              : t('carriersUi.kpi.otpOk', 'boîtes connectées')}
            contextTone={counts.missingOtp > 0 ? 'warning' : undefined}
            active={flag === 'missing_otp'}
            onClick={() => toggleFlag('missing_otp')}
          />
        ) : <span className="u-skeleton carriers-v2__kpi-skeleton" aria-hidden="true" />)}
      </div>
    );
  };

  const renderAccountCell = (facts) => {
    if (facts.needsAccount === false) return <span className="u-muted">—</span>;
    if (!facts.accountsLoaded) return <span className="u-skeleton carriers-v2__cell-skeleton" aria-hidden="true" />;
    if (facts.invalidAccounts.length > 0) {
      return (
        <span title={t('carriersUi.list.accountRefusedTitle', 'Le portail refuse un compte : mettez à jour son mot de passe.')}>
          <Badge tone="danger" dot>{t('carriersUi.list.accountRefused', 'Compte refusé')}</Badge>
        </span>
      );
    }
    if (facts.accounts.length === 0) {
      return facts.needsAccount
        ? <Badge tone="warning" dot>{t('carriersUi.list.accountMissing', 'Aucun compte')}</Badge>
        : <span className="u-muted">—</span>;
    }
    return (
      <Badge tone="success" dot>
        {tp('carriersUi.list.accountCount', '{{count}} compte', '{{count}} comptes', { count: facts.accounts.length })}
      </Badge>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="carriers-v2__skeleton" aria-busy="true" aria-label={t('carriersUi.list.loading', 'Chargement des transporteurs')}>
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (filtered.length === 0) {
      return searchQuery || flag ? (
        <EmptyState
          icon={Search}
          title={t('carriersUi.list.emptyFiltered', 'Aucun transporteur ne correspond')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateParams({ q: null, flag: null, page: null })}>
              {t('carriersUi.list.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('carriersUi.list.emptyFilteredHint', 'Changez la recherche ou le filtre pour voir les autres transporteurs.')}
        </EmptyState>
      ) : (
        <EmptyState
          icon={Truck}
          title={t('carriersUi.list.emptyTitle', 'Aucun transporteur pour l’instant')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleCreateButtonClick}>
              <Plus aria-hidden="true" />
              {t('carriersInstructions.addCarrier', 'Ajouter un transporteur')}
            </button>
          )}
        >
          {t('carriersUi.list.emptyHint', 'Ajoutez les transporteurs de vos colis : l’agent les contacte sur leur portail ou par e-mail pour vos réclamations.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table carriers-v2__table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>{t('carriersUi.list.colCarrier', 'Transporteur')}</th>
              <th style={{ width: '20%' }}>{t('carriersUi.list.colContact', 'Contact')}</th>
              <th style={{ width: '17%' }}>{t('carriersUi.list.colAccount', 'Compte portail')}</th>
              <th style={{ width: '15%' }}>{t('carriersUi.list.colTickets', 'Tickets 30 j')}</th>
              <th style={{ width: '17%' }}>{t('carriersUi.list.colAutomation', 'Automatisation')}</th>
              <th style={{ width: '62px' }} aria-label={t('carriersUi.open', 'Ouvrir')} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((carrier) => {
              const facts = factsById[carrier.id];
              const services = (facts.activity?.services || []).filter((s) => s.service_label).map((s) => s.service_label);
              const open = () => navigate(`/carriers-instructions/${carrier.id}`);
              return (
                <tr
                  key={carrier.id}
                  className="carriers-v2__row"
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                >
                  <td title={[carrier.name, ...services].join(' · ')}>
                    <span className="carriers-v2__carrier">
                      <CarrierLogo name={carrier.name} />
                      <span className="carriers-v2__carrier-text">
                        <span className="carriers-v2__carrier-name">{carrier.name}</span>
                        <span className="u-cell-sub">
                          {services.length > 0
                            ? services.join(' · ')
                            : (facts.missingRules ? t('carriersUi.list.noRules', 'sans consignes') : t('carriersUi.list.hasRules', 'consignes renseignées'))}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td title={facts.addresses.join(', ')}>
                    <ModeBadge mode={facts.mode} t={t} />
                    {facts.addresses.length > 0 && (
                      <span className="u-cell-sub">
                        {`${facts.addresses[0]}${facts.addresses.length > 1 ? ` +${facts.addresses.length - 1}` : ''}`}
                      </span>
                    )}
                    {facts.missingAddress && <span className="u-cell-sub">{t('carriersUi.list.noAddress', 'aucune adresse')}</span>}
                    {facts.addresses.length === 0 && carrier.form_tool && (
                      <span className="u-cell-sub">{t('carriersUi.list.formRouting', 'formulaire pour une partie des suivis')}</span>
                    )}
                  </td>
                  <td>
                    {renderAccountCell(facts)}
                    {facts.needsOtp && facts.otp && (
                      <span className="u-cell-sub">
                        {facts.otp.configured
                          ? t('carriersUi.list.otpOk', 'codes : connectés')
                          : t('carriersUi.list.otpMissing', 'codes : à connecter')}
                      </span>
                    )}
                  </td>
                  <td>
                    {stats === undefined ? <span className="u-skeleton carriers-v2__cell-skeleton" aria-hidden="true" /> : stats === null ? (
                      <span className="u-muted">—</span>
                    ) : (
                      <span className="carriers-v2__tickets">
                        <span className="u-num">{formatCount(facts.activity?.count || 0, lang)}</span>
                        <span className="u-meter carriers-v2__tickets-meter" aria-hidden="true">
                          <span style={{ width: `${((facts.activity?.count || 0) / maxTickets) * 100}%` }} />
                        </span>
                      </span>
                    )}
                  </td>
                  <td>
                    {carrier.automation
                      ? <Badge tone="success" dot>{t('carriersUi.list.automationOn', 'Activée')}</Badge>
                      : <Badge dot>{t('carriersUi.list.automationOff', 'Désactivée')}</Badge>}
                  </td>
                  <td className="carriers-v2__chevron"><ChevronRight aria-hidden="true" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderList = () => (
    <section className="u-card">
      <div className="u-toolbar">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            className="u-input"
            type="search"
            value={searchQuery}
            onChange={(e) => updateParams({ q: e.target.value, page: null }, { replace: true })}
            placeholder={t('carriersUi.list.search', 'Rechercher un transporteur')}
            aria-label={t('carriersUi.list.search', 'Rechercher un transporteur')}
          />
        </div>
        {!loading && counts.total > 0 && (
          <Segmented
            label={t('carriersUi.list.filters', 'Filtrer les transporteurs')}
            value={flag || 'all'}
            onChange={(value) => updateParams({ flag: value === 'all' ? null : value, page: null })}
            options={[
              { value: 'all', label: t('carriersUi.list.flagAll', 'Tous'), count: counts.total },
              counts.needAccount > 0 && { value: 'missing_credentials', label: flagLabel('missing_credentials'), count: counts.missingCredentials },
              counts.needOtp > 0 && { value: 'missing_otp', label: flagLabel('missing_otp'), count: counts.missingOtp },
              { value: 'missing_rules', label: flagLabel('missing_rules'), count: counts.missingRules },
            ].filter(Boolean)}
          />
        )}
        <span className="u-toolbar__spacer" />
        {!loading && (
          <span className="carriers-v2__count">
            {tp('carriersUi.list.count', '{{count}} transporteur', '{{count}} transporteurs', { count: filtered.length })}
          </span>
        )}
      </div>
      {renderTable()}
      {!loading && filtered.length > PAGE_SIZE && (
        <Pagination
          page={currentPage}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onChange={(next) => updateParams({ page: next <= 1 ? null : next })}
          summary={({ first, last, total }) => t('carriersUi.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total })}
          previousLabel={t('carriersUi.previousPage', 'Page précédente')}
          nextLabel={t('carriersUi.nextPage', 'Page suivante')}
        />
      )}
    </section>
  );

  const mailSenderIsAccount = mailSender?.source === 'account_mailbox';
  const instructionsChanged = (messageConfig?.general_instructions || '') !== generalInstructions.trim();

  const renderMessages = () => (
    <div className="carriers-v2__stack">
      <Card
        icon={FileText}
        title={t('carrierMessageConfig.title', 'Consignes des messages transporteurs')}
        badge={(messageConfig?.general_instructions || '').trim()
          ? <Badge tone="success" dot>{t('carriersUi.messages.custom', 'Personnalisées')}</Badge>
          : <Badge>{t('carriersUi.messages.default', 'Par défaut')}</Badge>}
        description={t('carriersUi.messages.desc', 'Appliquées à la rédaction de chaque message envoyé à un transporteur. Des consignes propres à un transporteur se mettent sur sa page.')}
        footerNote={t('carrierMessageConfig.hardConstraintsNote', 'Le numéro de suivi est toujours inclus automatiquement, et les messages Mondial Relay restent toujours sous {{limit}} caractères — quelles que soient ces instructions.', {
          limit: messageConfig?.hard_limits?.['Mondial Relay'] || 255,
        })}
        footer={(
          <button
            type="button"
            className="u-btn u-btn--primary"
            onClick={handleSaveMessageConfig}
            disabled={messageConfigSaving || !messageConfig || !instructionsChanged}
          >
            {messageConfigSaving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
          </button>
        )}
      >
        <div className="u-form">
          {messageConfigError && <Banner tone="danger">{messageConfigError}</Banner>}
          <Field
            label={t('carriersUi.messages.label', 'Consignes générales')}
            htmlFor="carriers-general-instructions"
            help={messageConfig?.default_general_instructions
              ? t('carriersUi.messages.defaultHelp', 'Vide = consignes par défaut : « {{text}} »', { text: messageConfig.default_general_instructions })
              : undefined}
          >
            <textarea
              id="carriers-general-instructions"
              className="u-textarea"
              value={generalInstructions}
              onChange={(e) => setGeneralInstructions(e.target.value)}
              rows={4}
              maxLength={messageConfig?.max_instructions_length || 2000}
              placeholder={t('carrierMessageConfig.placeholderGeneric', 'Consignes générales pour les messages transporteurs')}
              disabled={!messageConfig}
            />
          </Field>
        </div>
      </Card>

      <Card
        icon={Inbox}
        title={t('carrierMailSender.title', 'Boîte d’envoi des e-mails transporteur')}
        description={t('carriersUi.messages.senderDesc', 'Où partent les nouveaux échanges par e-mail avec les transporteurs, et où leurs réponses sont lues. Elle se choisit dans Paramètres.')}
        flush
      >
        <ul className="u-list">
          <li className="u-row">
            <span className="u-icon-chip u-icon-chip--sm" aria-hidden="true"><Mail strokeWidth={1.8} /></span>
            <div className="u-row__main">
              <div className="u-row__title">
                {mailSender === undefined ? <span className="u-skeleton carriers-v2__cell-skeleton" aria-hidden="true" />
                  : mailSenderIsAccount ? mailSender.email : t('carriersUi.messages.platform', 'Boîte de la plateforme')}
                {mailSender && (mailSenderIsAccount
                  ? (mailSender.problem
                    ? <Badge tone="danger" dot>{t('carriersUi.messages.senderBroken', 'En échec')}</Badge>
                    : <Badge tone="success" dot>{t('carriersUi.messages.senderAccount', 'Boîte du compte')}</Badge>)
                  : <Badge>{t('carrierMailSender.platformBadge', 'Plateforme')}</Badge>)}
              </div>
              <div className="u-row__meta u-row__meta--wrap">
                {mailSender === null
                  ? t('carriersUi.messages.senderUnknown', 'La boîte d’envoi n’a pas pu être lue.')
                  : mailSender?.problem || (mailSenderIsAccount
                    ? t('carriersUi.messages.senderAccountHint', 'Les nouveaux échanges partent de cette boîte, où leurs réponses sont lues. Les échanges déjà ouverts gardent la leur.')
                    : t('carriersUi.messages.senderPlatformHint', 'Connectez la boîte Gmail du compte dans Paramètres pour écrire aux transporteurs avec votre adresse.'))}
              </div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate('/parameters?tab=integrations')}>
                {t('carriersUi.attention.openParameters', 'Ouvrir Paramètres')}
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </li>
        </ul>
      </Card>
    </div>
  );

  // ------------------------------------------------------------ modals
  const catalogDetail = (entry) => (entry.detail ? t(`carriersInstructions.detail_${entry.detail}`, {
    portal_claims: 'Réclamations déposées sur le portail transporteur',
    form_claims: 'Réclamations via le formulaire du transporteur',
    api_claims: 'Réclamations via l’API du transporteur',
    mail_create_portal_updates: 'Premier contact par e-mail, relances sur le portail',
  }[entry.detail] || entry.detail) : null);

  const renderCatalogSection = (mode, icon, title, hint, tour) => {
    const entries = (carrierCatalog || []).filter((entry) => entry.integration_mode === mode);
    const Icon = icon;
    return (
      <section className="carriers-v2__catalog-section" data-tour={tour}>
        <div className="carriers-v2__catalog-head">
          <span className="u-icon-chip u-icon-chip--sm" aria-hidden="true"><Icon strokeWidth={1.8} /></span>
          <div>
            <div className="carriers-v2__catalog-title">{title}</div>
            <p className="u-help">{hint}</p>
          </div>
        </div>
        {carrierCatalog === null ? (
          <div className="carriers-v2__catalog-grid" aria-hidden="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton carriers-v2__catalog-skeleton" />)}
          </div>
        ) : (
          <div className="carriers-v2__catalog-grid">
            {entries.map((entry) => {
              const added = isCatalogCarrierAdded(entry);
              return (
                <button
                  key={entry.name}
                  type="button"
                  className="carriers-v2__catalog-tile"
                  onClick={() => handleSelectCatalogCarrier(entry)}
                  disabled={added}
                >
                  <CarrierLogo name={entry.name} size="sm" />
                  <span className="carriers-v2__catalog-text">
                    <span className="carriers-v2__catalog-name">
                      {entry.name}
                      {added && <Badge tone="success">{t('carriersInstructions.alreadyAdded', 'Ajouté')}</Badge>}
                    </span>
                    {catalogDetail(entry) && <span className="carriers-v2__catalog-detail">{catalogDetail(entry)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderPicker = () => (
    <Modal
      wide
      icon={Plus}
      title={t('carriersInstructions.addCarrier', 'Ajouter un transporteur')}
      description={t('carriersUi.add.pickerDesc', 'Choisissez le transporteur : son mode de contact est déjà connu.')}
      onClose={handleCloseModal}
      closeLabel={t('common.close', 'Fermer')}
    >
      <div className="carriers-v2__picker">
        {renderCatalogSection('web_automation', Globe,
          t('carriersUi.add.portalSection', 'Par le portail, un formulaire ou l’API'),
          t('carriersInstructions.webAutomationHint', 'Unanimity dépose et suit les réclamations directement sur le portail du transporteur. Des identifiants portail peuvent être requis.'),
          'catalog-web')}
        {renderCatalogSection('mail', Mail,
          t('carriersInstructions.mailSection', 'Transporteurs par e-mail'),
          t('carriersInstructions.mailHint', 'Unanimity contacte ces transporteurs par e-mail, via les adresses de contact que vous renseignez.'),
          'catalog-mail')}
        <section className="carriers-v2__catalog-section carriers-v2__request">
          <div className="carriers-v2__catalog-head">
            <span className="u-icon-chip u-icon-chip--sm u-icon-chip--neutral" aria-hidden="true"><Send strokeWidth={1.8} /></span>
            <div>
              <div className="carriers-v2__catalog-title">{t('carriersInstructions.missingCarrierTitle', 'Votre transporteur n’est pas dans la liste ?')}</div>
              <p className="u-help">{t('carriersInstructions.missingCarrierHint', 'Demandez-le et notre équipe construira l’intégration — ou ajoutez-le dès maintenant comme transporteur e-mail.')}</p>
            </div>
          </div>
          <div className="carriers-v2__request-actions">
            <button type="button" className="u-btn u-btn--secondary" onClick={() => handleSelectManualCreation()}>
              <Plus aria-hidden="true" />
              {t('carriersInstructions.addCustomCarrier', 'Ajouter un transporteur manuellement')}
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={() => openRequestModal({ type: 'new_carrier' })}>
              <Send aria-hidden="true" />
              {t('carriersInstructions.requestCarrier', 'Demander un transporteur')}
            </button>
          </div>
          {pendingRequests.length > 0 && (
            <div className="carriers-v2__pending">
              <span className="u-overline">{t('carriersUi.add.pending', 'Demandes en cours')}</span>
              {pendingRequests.map((request) => (
                <span key={request.id} className="u-tag">
                  {request.carrier_name}
                  {request.request_type === 'custom_integration' ? ` · ${t('carriersInstructions.modeCustom', 'personnalisé')}` : ''}
                </span>
              ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );

  const renderCreate = () => (
    <Modal
      icon={newCarrier.isAutomated ? Globe : Mail}
      title={newCarrier.fromCatalog
        ? t('carriersUi.add.createTitleNamed', 'Ajouter {{name}}', { name: newCarrier.name })
        : t('carriersUi.add.createTitle', 'Ajouter un transporteur')}
      description={newCarrier.fromCatalog
        ? (newCarrier.isAutomated
          ? t('carriersUi.add.webBanner', 'L’agent dépose les réclamations directement sur le portail {{name}}.', { name: newCarrier.name })
          : t('carriersUi.add.mailBanner', 'L’agent écrit à {{name}} aux adresses que vous ajoutez ci-dessous.', { name: newCarrier.name }))
        : t('carriersUi.add.manualDesc', 'Un transporteur hors catalogue, contacté par e-mail.')}
      onClose={handleCloseModal}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={handleCloseModal} disabled={isCreating}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            type="button"
            className="u-btn u-btn--primary"
            data-tour="carrier-create"
            onClick={handleCreateCarrier}
            disabled={isCreating || !newCarrier.name.trim()}
          >
            {isCreating ? t('common.saving', 'Enregistrement…') : t('carriersUi.add.create', 'Ajouter')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {createError && <Banner tone="danger">{createError}</Banner>}
        <Field
          label={t('carriersInstructions.name', 'Nom')}
          htmlFor="new-carrier-name"
          help={newCarrier.fromCatalog ? t('carriersUi.add.nameLocked', 'Le nom d’un transporteur du catalogue est fixe : c’est lui qui relie l’agent au bon portail.') : undefined}
        >
          <input
            id="new-carrier-name"
            type="text"
            className="u-input"
            value={newCarrier.name}
            onChange={(e) => setNewCarrier({ ...newCarrier, name: e.target.value })}
            readOnly={newCarrier.fromCatalog}
          />
        </Field>

        {!newCarrier.isAutomated && (
          <div className="u-field" data-tour="carrier-emails">
            <span className="u-label">{t('carriersUi.add.emails', 'Adresses e-mail du transporteur')}</span>
            {newCarrier.emails.map((value, index) => (
              <div key={index} className="carriers-v2__email-row">
                <input
                  type="email"
                  className="u-input"
                  value={value}
                  onChange={(e) => setNewEmails(newCarrier.emails.map((v, i) => (i === index ? e.target.value : v)))}
                  placeholder={t('carriersUi.add.emailPlaceholder', 'litiges@transporteur.fr')}
                  aria-label={t('carriersUi.add.emailN', 'Adresse {{n}}', { n: index + 1 })}
                />
                <button
                  type="button"
                  className="u-btn u-btn--ghost u-btn--icon"
                  onClick={() => setNewEmails(newCarrier.emails.filter((_, i) => i !== index))}
                  aria-label={t('carriersUi.removeAddress', 'Retirer l’adresse')}
                  title={t('carriersUi.removeAddress', 'Retirer l’adresse')}
                >
                  <X aria-hidden="true" />
                </button>
              </div>
            ))}
            <div>
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setNewEmails([...newCarrier.emails, ''])}>
                <Plus aria-hidden="true" />
                {t('carriersUi.addAddress', 'Ajouter une adresse')}
              </button>
            </div>
          </div>
        )}

        <Field
          label={t('carriersInstructions.contactRules', 'Instructions de contact')}
          htmlFor="new-carrier-rules"
          help={t('carriersUi.add.rulesHelp', 'Ce que l’agent doit savoir pour contacter ce transporteur (portail, pièces à joindre, délais).')}
        >
          <textarea
            id="new-carrier-rules"
            className="u-textarea"
            value={newCarrier.contact_rules}
            onChange={(e) => setNewCarrier({ ...newCarrier, contact_rules: e.target.value })}
            rows={4}
          />
        </Field>
        <Field label={t('carriersInstructions.messageInstructions', 'Consignes de message pour ce transporteur (optionnel)')} htmlFor="new-carrier-message-instructions">
          <textarea
            id="new-carrier-message-instructions"
            className="u-textarea"
            value={newCarrier.message_instructions}
            onChange={(e) => setNewCarrier({ ...newCarrier, message_instructions: e.target.value })}
            rows={3}
          />
        </Field>

        <div className="carriers-v2__switches">
          <div data-tour="carrier-automation">
            <Switch
              id="new-carrier-automation"
              checked={newCarrier.automation}
              onChange={(checked) => setNewCarrier({ ...newCarrier, automation: checked })}
              label={t('carriersList.automationEnabled', 'Automatisation activée pour ce transporteur')}
            />
          </div>
          {/* The integration mode is fixed for catalog carriers. */}
          {!newCarrier.fromCatalog && (
            <Switch
              id="new-carrier-is-mail"
              checked={newCarrier.is_mail}
              onChange={(checked) => setNewCarrier({ ...newCarrier, is_mail: checked })}
              label={t('carriersList.isMailLabel', 'Transporteur contacté par e-mail')}
            />
          )}
        </div>

        {newCarrier.requiresCredentials && (
          <div className="u-field" data-tour="carrier-credentials">
            <span className="u-label">{t('carriersUi.add.portalAccount', 'Compte portail')}</span>
            <p className="u-help">{t('carriersInstructions.credentialsHint', 'Utilisés par l’automatisation pour se connecter au portail du transporteur. Vous pouvez aussi les ajouter plus tard sur la page du transporteur.')}</p>
            <div className="u-form-grid">
              <input
                type="text"
                className="u-input"
                value={newCarrier.credentials.username}
                onChange={(e) => setNewCarrier({ ...newCarrier, credentials: { ...newCarrier.credentials, username: e.target.value } })}
                placeholder={t('carriersUi.username', 'Identifiant')}
                aria-label={t('carriersUi.username', 'Identifiant')}
                autoComplete="off"
              />
              <input
                type="password"
                className="u-input"
                value={newCarrier.credentials.password}
                onChange={(e) => setNewCarrier({ ...newCarrier, credentials: { ...newCarrier.credentials, password: e.target.value } })}
                placeholder={t('carriersUi.password', 'Mot de passe')}
                aria-label={t('carriersUi.password', 'Mot de passe')}
                autoComplete="new-password"
              />
            </div>
          </div>
        )}

        {newCarrier.fromCatalog && (
          <button
            type="button"
            className="u-link carriers-v2__link-button"
            onClick={() => {
              setShowCreateModal(false);
              openRequestModal({ type: 'custom_integration', carrierName: newCarrier.name, nameLocked: true });
            }}
          >
            {t('carriersUi.add.customIntegration', 'Votre façon de travailler avec {{name}} est différente (formulaires dédiés, portail spécifique) ? Demandez une intégration personnalisée.', { name: newCarrier.name })}
          </button>
        )}
      </div>
    </Modal>
  );

  const renderRequest = () => (
    <Modal
      icon={Send}
      title={requestModal.type === 'new_carrier'
        ? t('carriersInstructions.requestModalTitleNew', 'Demander un nouveau transporteur')
        : t('carriersInstructions.requestModalTitleCustom', 'Demander une intégration personnalisée')}
      description={requestModal.type === 'new_carrier'
        ? t('carriersInstructions.requestNewNote', 'Indiquez-nous le transporteur avec lequel vous travaillez — notre équipe étudiera son intégration.')
        : t('carriersInstructions.requestCustomNote', 'Décrivez en quoi votre processus diffère de l’intégration standard (formulaires de réclamation dédiés, portail spécifique, contact dédié…).')}
      onClose={() => setRequestModal(null)}
      closeLabel={t('common.close', 'Fermer')}
      footer={requestStatus === 'sent' ? (
        <button type="button" className="u-btn u-btn--primary" onClick={() => setRequestModal(null)}>
          {t('common.close', 'Fermer')}
        </button>
      ) : (
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setRequestModal(null)}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            type="button"
            className="u-btn u-btn--primary"
            onClick={handleSubmitCarrierRequest}
            disabled={requestStatus === 'sending' || !requestCarrierName.trim()}
          >
            <Send aria-hidden="true" />
            {requestStatus === 'sending' ? t('carriersUi.sending', 'Envoi…') : t('carriersInstructions.sendRequest', 'Envoyer la demande')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        <Field label={t('carriersInstructions.name', 'Nom')} htmlFor="request-carrier-name">
          <input
            id="request-carrier-name"
            type="text"
            className="u-input"
            value={requestCarrierName}
            onChange={(e) => setRequestCarrierName(e.target.value)}
            readOnly={requestModal.nameLocked}
            maxLength={100}
          />
        </Field>
        <Field label={t('carriersUi.add.requestDetails', 'Détails')} htmlFor="request-carrier-details">
          <textarea
            id="request-carrier-details"
            className="u-textarea"
            value={requestDetails}
            onChange={(e) => setRequestDetails(e.target.value)}
            placeholder={t('carriersInstructions.requestDetailsPlaceholder', 'Décrivez comment vous travaillez avec ce transporteur (portail, formulaires, adresses de contact, volumes…)')}
            rows={4}
            maxLength={2000}
          />
        </Field>
        {requestStatus === 'sent' && <Banner tone="success">{t('carriersInstructions.requestSent', 'Demande envoyée — notre équipe revient vers vous.')}</Banner>}
        {requestStatus === 'duplicate' && <Banner tone="warning">{t('carriersInstructions.requestAlreadyPending', 'Une demande pour ce transporteur est déjà en attente.')}</Banner>}
        {requestStatus === 'error' && <Banner tone="danger">{t('carriersInstructions.requestFailed', 'Échec de l’envoi de la demande. Réessayez.')}</Banner>}
      </div>
    </Modal>
  );

  return (
    <div className="u-page carriers-v2">
      <PageHeader
        title={t('carriersInstructions.pageTitle', 'Transporteurs')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={refresh}
              title={t('carriersUi.refresh', 'Actualiser')}
              aria-label={t('carriersUi.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button type="button" className="u-btn u-btn--primary" data-tour="carriers-add" onClick={handleCreateButtonClick}>
              <Plus aria-hidden="true" />
              {t('carriersInstructions.addCarrier', 'Ajouter un transporteur')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {loadError && <Banner tone="danger">{loadError}</Banner>}
        {renderAttention()}
        {renderKpis()}
        <Tabs
          label={t('carriersInstructions.pageTitle', 'Transporteurs')}
          active={view}
          onChange={(next) => updateParams({ view: next === 'list' ? null : next })}
          tabs={[
            { key: 'list', label: t('carriersUi.tabs.list', 'Transporteurs'), icon: Truck, count: loading ? undefined : formatCount(counts.total, lang) },
            { key: 'messages', label: t('carriersUi.tabs.messages', 'Envoi des messages'), icon: Send },
          ]}
        />
        <div role="tabpanel">
          {view === 'list' ? renderList() : renderMessages()}
        </div>
      </div>

      {showSelectionOverlay && renderPicker()}
      {showCreateModal && renderCreate()}
      {requestModal && renderRequest()}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default Carriers;
